import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../services/api';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import RecompensaAbate from '../components/RecompensaAbate';
import FechamentoPorEmitente from '../components/FechamentoPorEmitente';
import authService from '../services/authService';
import './FaturadoDetalhes.css';

function FaturadoDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatura, setFatura] = useState(null);
  const [ordensServico, setOrdensServico] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [impostos, setImpostos] = useState(null);
  const [previsaoRecebimento, setPrevisaoRecebimento] = useState('');
  const [editandoPrevisao, setEditandoPrevisao] = useState(false);
  const [dataVencimento, setDataVencimento] = useState('');
  const [editandoVencimento, setEditandoVencimento] = useState(false);
  const [filtros, setFiltros] = useState({
    busca: '',
    status: ''
  });

  /*
   * O COMPROVANTE DENTRO DA OS.
   *
   * Ele já existia no banco (`fatura.ordensServico[].comprovante`, gravado por
   * `sincronizarFaturaComOrdensPagas`) e já tinha endpoint
   * (`GET /pagamentos/:faturaId/os/:osId/comprovante`). O que não existia era
   * como chegar nele a partir daqui: quem abria a fatura via "Faturados" via a
   * OS como paga e não tinha onde conferir o pagamento.
   *
   * ⚠️ BUSCA SOB DEMANDA, uma OS por vez. O comprovante é base64 de alguns MB;
   * trazer todos no carregamento da fatura é o que já estourou memória na
   * listagem de pagamentos (ver o `.select('-comprovante')` de
   * `ordemPagamentoController.listar`).
   */
  const [comprovante, setComprovante] = useState(null);
  const [carregandoComprovante, setCarregandoComprovante] = useState(null);

  const percentualTaxaOperacao = useMemo(() => {
    if (!fatura) return null;
    // Fatura de abastecimento usa taxa por litro (não percentual) — não recalcular
    if (fatura.origem === 'abastecimento') return null;
    const valorTaxas = fatura.valorTaxasOperacao || 0;
    if (valorTaxas <= 0) return null;

    // Primeiro: tentar usar a taxa salva na OS
    const osComTaxa = ordensServico.find(item => {
      const taxa = item?.ordemServico?.taxaAplicada;
      return typeof taxa === 'number' && taxa > 0;
    });
    if (osComTaxa) {
      return osComTaxa.ordemServico.taxaAplicada;
    }

    // Segundo: tentar usar a taxa do cliente
    const taxaCliente = fatura.cliente?.taxaOperacao;
    if (typeof taxaCliente === 'number' && taxaCliente > 0) {
      return taxaCliente;
    }

    const taxasAntecipacao = fatura.cliente?.taxasAntecipacao;
    if (taxasAntecipacao) {
      const { aVista, aposFechamento, aprazado } = taxasAntecipacao;
      const candidatos = [aVista, aposFechamento, aprazado].filter(valor => typeof valor === 'number' && valor > 0);
      if (candidatos.length > 0) {
        return candidatos[0];
      }
    }

    // Fallback: calcular a partir dos valores (base = valor após impostos)
    const base = (fatura.valorComDesconto || 0) - (fatura.valorImpostos || 0);
    if (base > 0) {
      const perc = (valorTaxas / base) * 100;
      if (Number.isFinite(perc)) {
        return perc;
      }
    }

    return null;
  }, [fatura, ordensServico]);

  // Calcular fator de proporção para valor líquido
  const fatorLiquido = useMemo(() => {
    if (!fatura) return 1;
    const valorComDesconto = fatura.valorComDesconto || 0;
    const valorDevido = fatura.valorDevido || 0;
    if (valorComDesconto > 0) {
      return valorDevido / valorComDesconto;
    }
    return 1;
  }, [fatura]);

  // Calcular valores líquidos proporcionais de cada OS
  const ordensComValorLiquido = useMemo(() => {
    return ordensServico.map(item => ({
      ...item,
      valorLiquido: Math.round((item.valorOS || 0) * fatorLiquido * 100) / 100
    }));
  }, [ordensServico, fatorLiquido]);

  // Resumo das OS selecionadas
  const resumoSelecionadas = useMemo(() => {
    if (selecionadas.length === 0 || !fatura) {
      return null;
    }
    
    const osSelecionadas = ordensComValorLiquido.filter(
      item => selecionadas.includes(item.ordemServico._id) && item.statusPagamento === 'Aguardando pagamento'
    );
    
    const valorBruto = osSelecionadas.reduce((acc, item) => acc + (item.valorOS || 0), 0);
    const valorLiquido = osSelecionadas.reduce((acc, item) => acc + (item.valorLiquido || 0), 0);
    const taxasProporcional = valorBruto - valorLiquido;
    
    return {
      quantidade: osSelecionadas.length,
      valorBruto: Math.round(valorBruto * 100) / 100,
      valorLiquido: Math.round(valorLiquido * 100) / 100,
      taxasProporcional: Math.round(taxasProporcional * 100) / 100
    };
  }, [selecionadas, ordensComValorLiquido, fatura]);

  // Verificar se há OS pendentes
  const osPendentes = useMemo(() => {
    return ordensServico.filter(item => item.statusPagamento === 'Aguardando pagamento');
  }, [ordensServico]);

  // Verificar se é fornecedor ou cliente (somente leitura)
  const isFornecedor = user?.role === 'fornecedor';
  const isCliente = user?.role === 'cliente';
  const isReadOnly = isFornecedor || isCliente;

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    loadFatura();
    loadImpostos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadImpostos = async () => {
    try {
      const response = await api.get('/impostos-retencoes');
      
      // Verifica se response.data é um array ou um objeto único
      if (Array.isArray(response.data) && response.data.length > 0) {
        const impostosAtivos = response.data.find(i => i.ativo) || response.data[0];
        setImpostos(impostosAtivos);
      } else if (response.data && typeof response.data === 'object') {
        // Se retornar um objeto único
        setImpostos(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar impostos:', error);
    }
  };

  const loadFatura = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/faturas/${id}`);
      console.log('📊 Fatura carregada:', response.data);
      console.log('📅 Previsão recebida:', response.data.previsaoRecebimento);
      setFatura(response.data);
      setOrdensServico(response.data.ordensServico || []);
      
      // Carregar previsão de recebimento se existir
      if (response.data.previsaoRecebimento) {
        const data = new Date(response.data.previsaoRecebimento);
        // Usar UTC para garantir que a data não mude
        const ano = data.getUTCFullYear();
        const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
        const dia = String(data.getUTCDate()).padStart(2, '0');
        const dataFormatada = `${ano}-${mes}-${dia}`;
        console.log('✅ Previsão setada:', dataFormatada);
        setPrevisaoRecebimento(dataFormatada);
      } else {
        console.log('⚠️ Nenhuma previsão encontrada');
        setPrevisaoRecebimento('');
      }
      
      // Carregar data de vencimento se existir
      if (response.data.dataVencimento) {
        const data = new Date(response.data.dataVencimento);
        const ano = data.getUTCFullYear();
        const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
        const dia = String(data.getUTCDate()).padStart(2, '0');
        const dataFormatada = `${ano}-${mes}-${dia}`;
        console.log('✅ Vencimento setado:', dataFormatada);
        setDataVencimento(dataFormatada);
      } else {
        console.log('⚠️ Nenhum vencimento encontrado');
        setDataVencimento('');
      }
    } catch (error) {
      toast.error('Erro ao carregar fatura');
      console.error(error);
      navigate('/faturados');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const handleSelecionar = (osId) => {
    const os = ordensServico.find(item => item.ordemServico._id === osId);
    
    // Não permitir selecionar OS já pagas
    if (os && os.statusPagamento === 'Paga') {
      return;
    }

    setSelecionadas(prev => {
      if (prev.includes(osId)) {
        return prev.filter(id => id !== osId);
      } else {
        return [...prev, osId];
      }
    });
  };

  const handleSelecionarTodas = () => {
    const osPendentes = ordensServico
      .filter(item => item.statusPagamento === 'Aguardando pagamento')
      .map(item => item.ordemServico._id);
    
    if (selecionadas.length === osPendentes.length) {
      setSelecionadas([]);
    } else {
      setSelecionadas(osPendentes);
    }
  };

  const handleMarcarComoPagas = async () => {
    if (selecionadas.length === 0) {
      toast.warning('Selecione pelo menos uma ordem de serviço');
      return;
    }

    try {
      const promises = selecionadas.map(osId =>
        api.patch(`/faturas/${id}/ordem-servico/${osId}/pagar`, {
          dataPagamento: new Date()
        })
      );

      await Promise.all(promises);
      toast.success('Ordens de serviço marcadas como pagas');
      setSelecionadas([]);
      loadFatura();
    } catch (error) {
      toast.error('Erro ao marcar como pagas');
      console.error(error);
    }
  };

  // Pagar fatura inteira (todas as OS pendentes)
  const handlePagarFaturaInteira = async () => {
    if (osPendentes.length === 0) {
      toast.info('Todas as ordens de serviço já foram pagas');
      return;
    }

    const valorRestante = fatura?.valorRestante || 0;
    if (!window.confirm(`Deseja marcar TODAS as ${osPendentes.length} ordens de serviço como pagas?\n\nValor total a pagar: ${formatarValor(valorRestante)}`)) {
      return;
    }

    try {
      await api.patch(`/faturas/${id}/pagar-tudo`, {
        dataPagamento: new Date()
      });

      toast.success('Fatura paga integralmente!');
      setSelecionadas([]);
      loadFatura();
    } catch (error) {
      toast.error('Erro ao pagar fatura');
      console.error(error);
    }
  };

  // Abre o comprovante de UMA ordem de serviço paga desta fatura.
  const abrirComprovante = async (item) => {
    const os = item?.ordemServico;
    const osId = os?._id || os?.id;
    if (!osId) {
      toast.error('Não foi possível identificar a ordem de serviço');
      return;
    }

    setCarregandoComprovante(osId);
    try {
      const res = await api.get(`/pagamentos/${id}/os/${osId}/comprovante`);
      const conteudo = res.data?.comprovante;

      if (!conteudo) {
        toast.info('Esta ordem de serviço não tem comprovante anexado');
        return;
      }

      setComprovante({
        osId,
        codigo: os.codigo,
        numeroOrdemServico: os.numeroOrdemServico,
        valorOS: item.valorOS,
        dataPagamento: item.dataPagamento,
        conteudo
      });
    } catch (error) {
      /*
       * 404 aqui NÃO é falha: o endpoint devolve 404 tanto para "a OS não está
       * nesta fatura" quanto para "não há comprovante anexado" — e o segundo é
       * o caso comum, de OS quitada antes de o anexo passar a ser exigido.
       *
       * Mostrar isso como erro vermelho treina a pessoa a ignorar o aviso. A
       * mensagem do backend já diz qual dos dois é; repassamos ela.
       */
      if (error.response?.status === 404) {
        toast.info(error.response?.data?.message || 'Comprovante não anexado');
        return;
      }
      console.error('Erro ao carregar comprovante:', error);
      toast.error(error.response?.data?.message || 'Erro ao carregar o comprovante');
    } finally {
      setCarregandoComprovante(null);
    }
  };

  const handleRemoverOS = async (osId) => {
    if (!window.confirm('Deseja realmente remover esta ordem de serviço da fatura?')) {
      return;
    }

    try {
      await api.delete(`/faturas/${id}/ordem-servico/${osId}`);
      toast.success('Ordem de serviço removida da fatura');
      loadFatura();
    } catch (error) {
      toast.error('Erro ao remover ordem de serviço');
      console.error(error);
    }
  };

  const handleExcluirFatura = async () => {
    if (!window.confirm('Deseja realmente excluir esta fatura? As ordens de serviço voltarão ao status "Autorizada".')) {
      return;
    }

    try {
      await api.delete(`/faturas/${id}`);
      toast.success('Fatura excluída com sucesso');
      navigate('/faturados');
    } catch (error) {
      toast.error('Erro ao excluir fatura');
      console.error(error);
    }
  };

  const handleSalvarPrevisao = async () => {
    try {
      console.log('💾 Salvando previsão:', previsaoRecebimento);
      
      let dataParaEnviar = null;
      if (previsaoRecebimento) {
        // Criar data no timezone local para evitar conversão UTC
        const [ano, mes, dia] = previsaoRecebimento.split('-');
        dataParaEnviar = new Date(ano, mes - 1, dia, 12, 0, 0); // Meio-dia para evitar problemas de timezone
      }
      
      const payload = {
        previsaoRecebimento: dataParaEnviar
      };
      console.log('📦 Payload enviado:', payload);
      
      const response = await api.put(`/faturas/${id}`, payload);
      console.log('✅ Resposta do servidor:', response.data);
      
      toast.success('Previsão de recebimento atualizada');
      setEditandoPrevisao(false);
      await loadFatura();
    } catch (error) {
      toast.error('Erro ao atualizar previsão');
      console.error('❌ Erro ao salvar:', error.response?.data || error);
    }
  };

  const handleCancelarPrevisao = () => {
    if (fatura.previsaoRecebimento) {
      const data = new Date(fatura.previsaoRecebimento);
      const ano = data.getUTCFullYear();
      const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
      const dia = String(data.getUTCDate()).padStart(2, '0');
      setPrevisaoRecebimento(`${ano}-${mes}-${dia}`);
    } else {
      setPrevisaoRecebimento('');
    }
    setEditandoPrevisao(false);
  };

  const handleSalvarVencimento = async () => {
    try {
      console.log('💾 Salvando vencimento:', dataVencimento);
      
      let dataParaEnviar = null;
      if (dataVencimento) {
        const [ano, mes, dia] = dataVencimento.split('-');
        dataParaEnviar = new Date(ano, mes - 1, dia, 12, 0, 0);
      }
      
      const payload = {
        dataVencimento: dataParaEnviar
      };
      console.log('📦 Payload enviado:', payload);
      
      const response = await api.put(`/faturas/${id}`, payload);
      console.log('✅ Resposta do servidor:', response.data);
      
      toast.success('Data de vencimento atualizada');
      setEditandoVencimento(false);
      await loadFatura();
    } catch (error) {
      toast.error('Erro ao atualizar vencimento');
      console.error('❌ Erro ao salvar:', error.response?.data || error);
    }
  };

  const handleCancelarVencimento = () => {
    if (fatura.dataVencimento) {
      const data = new Date(fatura.dataVencimento);
      const ano = data.getUTCFullYear();
      const mes = String(data.getUTCMonth() + 1).padStart(2, '0');
      const dia = String(data.getUTCDate()).padStart(2, '0');
      setDataVencimento(`${ano}-${mes}-${dia}`);
    } else {
      setDataVencimento('');
    }
    setEditandoVencimento(false);
  };

  const ordemFiltradas = ordensServico.filter(item => {
    const os = item.ordemServico;
    const matchBusca = !filtros.busca ||
      os.numeroOrdemServico?.toLowerCase().includes(filtros.busca.toLowerCase()) ||
      os.codigo?.toLowerCase().includes(filtros.busca.toLowerCase());
    
    const matchStatus = !filtros.status || item.statusPagamento === filtros.status;
    
    return matchBusca && matchStatus;
  });

  const formatarData = (data) => {
    if (!data) return '-';
    const date = new Date(data);
    // Usar UTC para evitar problemas de timezone
    return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const formatarValor = (valor) => {
    if (!valor && valor !== 0) return 'R$ 0,00';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatarPercentual = (valor) => {
    if (!valor && valor !== 0) return '0,00';
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getTextoTaxaOperacao = () => {
    if (isAbastecimento) {
      const totalLitros = (fatura?.abastecimentosVinculados || []).reduce((acc, item) => {
        const ab = item.abastecimento || item;
        return acc + (ab.litrosAbastecidos || 0);
      }, 0);
      const valorTaxa = fatura?.valorTaxasOperacao || 0;
      const taxaPorLitro = totalLitros > 0 ? Math.round((valorTaxa / totalLitros) * 100) / 100 : 0.08;
      return `(-) Taxa da Plataforma (R$ ${taxaPorLitro.toFixed(2).replace('.', ',')}/L × ${totalLitros.toFixed(2).replace('.', ',')} L)`;
    }
    if (percentualTaxaOperacao === null) {
      return '(-) Taxa de Operação';
    }
    return `(-) Taxa de Operação (${formatarPercentual(percentualTaxaOperacao)}%)`;
  };

  const calcularDetalhamentoImpostos = () => {
    if (!impostos || !fatura) {
      return { total: 0, detalhamento: [] };
    }

    // Primeiro, agregar todos os valores de peças e serviços
    let totalValorPecas = 0;
    let totalValorServico = 0;
    let temFornecedorNaoOptante = false;
    let tiposImposto = new Set();

    ordensServico.forEach(item => {
      const ordem = item.ordemServico;
      const cliente = ordem.cliente;
      const fornecedor = ordem.fornecedor;

      // Só calcular se fornecedor é Não Optante
      if (fornecedor?.naoOptanteSimples) {
        temFornecedorNaoOptante = true;
        totalValorPecas += ordem.valorPecasComDesconto || 0;
        totalValorServico += ordem.valorServicoComDesconto || 0;
        
        // Coletar tipos de imposto do cliente
        if (cliente?.tipoImposto && Array.isArray(cliente.tipoImposto)) {
          cliente.tipoImposto.forEach(tipo => tiposImposto.add(tipo));
        }
      }
    });

    if (!temFornecedorNaoOptante || tiposImposto.size === 0) {
      return { total: 0, detalhamento: [] };
    }

    const detalhamento = [];
    let total = 0;

    // Agora calcular impostos sobre os totais agregados
    tiposImposto.forEach(tipo => {
      try {
        switch (tipo) {
          case 'municipais':
            if (impostos.impostosMunicipais) {
              const percIRPecas = impostos.impostosMunicipais.pecas?.ir || 0;
              const percIRServicos = impostos.impostosMunicipais.servicos?.ir || 0;
              const irMunPecas = Math.round(totalValorPecas * percIRPecas) / 100;
              const irMunServicos = Math.round(totalValorServico * percIRServicos) / 100;
              const totalIR = irMunPecas + irMunServicos;
              
              if (totalIR > 0) {
                total += totalIR;
                const subitens = [];
                if (irMunPecas > 0) {
                  subitens.push({ texto: `Peças: ${formatarValor(totalValorPecas)} × ${formatarPercentual(percIRPecas)}% = ${formatarValor(irMunPecas)}`, valor: irMunPecas });
                }
                if (irMunServicos > 0) {
                  subitens.push({ texto: `Serviços: ${formatarValor(totalValorServico)} × ${formatarPercentual(percIRServicos)}% = ${formatarValor(irMunServicos)}`, valor: irMunServicos });
                }
                if (subitens.length > 0) {
                  const tituloPartes = [];
                  if (irMunPecas > 0) tituloPartes.push(`${formatarPercentual(percIRPecas)}% Peças`);
                  if (irMunServicos > 0) tituloPartes.push(`${formatarPercentual(percIRServicos)}% Serviços`);
                  detalhamento.push({
                    titulo: `IR Municipal (${tituloPartes.join(' + ')})`,
                    valor: totalIR,
                    subitens
                  });
                }
              }
            }
            break;
          case 'estaduais':
            if (impostos.impostosEstaduais) {
              const estaduaisPecas = impostos.impostosEstaduais.pecas || {};
              const estaduaisServicos = impostos.impostosEstaduais.servicos || {};
              const percTotalPecas = (estaduaisPecas.ir || 0) + (estaduaisPecas.pis || 0) + (estaduaisPecas.cofins || 0) + (estaduaisPecas.csll || 0);
              const percTotalServicos = (estaduaisServicos.ir || 0) + (estaduaisServicos.pis || 0) + (estaduaisServicos.cofins || 0) + (estaduaisServicos.csll || 0);
              const totalEstPecas = Math.round(totalValorPecas * percTotalPecas) / 100;
              const totalEstServicos = Math.round(totalValorServico * percTotalServicos) / 100;
              const totalEst = totalEstPecas + totalEstServicos;
              
              if (totalEst > 0) {
                total += totalEst;
                const subitens = [];
                if (totalEstPecas > 0) {
                  subitens.push({ texto: `Peças: ${formatarValor(totalValorPecas)} × ${formatarPercentual(percTotalPecas)}% = ${formatarValor(totalEstPecas)}`, valor: totalEstPecas });
                }
                if (totalEstServicos > 0) {
                  subitens.push({ texto: `Serviços: ${formatarValor(totalValorServico)} × ${formatarPercentual(percTotalServicos)}% = ${formatarValor(totalEstServicos)}`, valor: totalEstServicos });
                }
                if (subitens.length > 0) {
                  const tituloPartes = [];
                  if (totalEstPecas > 0) tituloPartes.push(`${formatarPercentual(percTotalPecas)}% Peças`);
                  if (totalEstServicos > 0) tituloPartes.push(`${formatarPercentual(percTotalServicos)}% Serviços`);
                  detalhamento.push({
                    titulo: `Impostos Estaduais (${tituloPartes.join(' + ')})`,
                    valor: totalEst,
                    subitens
                  });
                }
              }
            }
            break;
          case 'federais':
            if (impostos.impostosFederais) {
              const federaisPecas = impostos.impostosFederais.pecas || {};
              const federaisServicos = impostos.impostosFederais.servicos || {};
              const percTotalPecas = (federaisPecas.ir || 0) + (federaisPecas.pis || 0) + (federaisPecas.cofins || 0) + (federaisPecas.csll || 0);
              const percTotalServicos = (federaisServicos.ir || 0) + (federaisServicos.pis || 0) + (federaisServicos.cofins || 0) + (federaisServicos.csll || 0);
              const totalFedPecas = Math.round(totalValorPecas * percTotalPecas) / 100;
              const totalFedServicos = Math.round(totalValorServico * percTotalServicos) / 100;
              const totalFed = totalFedPecas + totalFedServicos;
              
              if (totalFed > 0) {
                total += totalFed;
                const subitens = [];
                if (totalFedPecas > 0) {
                  subitens.push({ texto: `Peças: ${formatarValor(totalValorPecas)} × ${formatarPercentual(percTotalPecas)}% = ${formatarValor(totalFedPecas)}`, valor: totalFedPecas });
                }
                if (totalFedServicos > 0) {
                  subitens.push({ texto: `Serviços: ${formatarValor(totalValorServico)} × ${formatarPercentual(percTotalServicos)}% = ${formatarValor(totalFedServicos)}`, valor: totalFedServicos });
                }
                if (subitens.length > 0) {
                  const tituloPartes = [];
                  if (totalFedPecas > 0) tituloPartes.push(`${formatarPercentual(percTotalPecas)}% Peças`);
                  if (totalFedServicos > 0) tituloPartes.push(`${formatarPercentual(percTotalServicos)}% Serviços`);
                  detalhamento.push({
                    titulo: `Impostos Federais (${tituloPartes.join(' + ')})`,
                    valor: totalFed,
                    subitens
                  });
                }
              }
            }
            break;
          case 'retencoes':
            if (impostos.retencoesOrgao) {
              const percRetencoes = impostos.retencoesOrgao.percentual || 0;
              const retencoes = Math.round((totalValorPecas + totalValorServico) * percRetencoes) / 100;
              
              if (retencoes > 0) {
                total += retencoes;
                detalhamento.push({
                  titulo: `Retenções Órgão (${percRetencoes}%)`,
                  valor: retencoes,
                  subitens: [
                    { texto: `Total (Peças + Serviços): ${formatarValor(totalValorPecas + totalValorServico)} × ${percRetencoes}% = ${formatarValor(retencoes)}`, valor: retencoes }
                  ]
                });
              }
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(`Erro ao calcular imposto ${tipo}:`, error);
      }
    });

    return { total, detalhamento };
  };

  const getStatusBadgeClass = (status) => {
    return status === 'Paga' ? 'status-badge status-paga' : 'status-badge status-aguardando';
  };

  const calcularDetalhamentoImpostosPecas = () => {
    if (!impostos || !fatura) return { total: 0, detalhamento: [] };

    // Primeiro, agregar todos os valores de peças
    let totalValorPecas = 0;
    let temFornecedorNaoOptante = false;
    let tiposImposto = new Set();

    ordensServico.forEach(item => {
      const ordem = item.ordemServico;
      const cliente = ordem.cliente;
      const fornecedor = ordem.fornecedor;

      if (fornecedor?.naoOptanteSimples) {
        temFornecedorNaoOptante = true;
        totalValorPecas += ordem.valorPecasComDesconto || 0;
        
        if (cliente?.tipoImposto && Array.isArray(cliente.tipoImposto)) {
          cliente.tipoImposto.forEach(tipo => tiposImposto.add(tipo));
        }
      }
    });

    if (!temFornecedorNaoOptante || tiposImposto.size === 0) {
      return { total: 0, detalhamento: [] };
    }

    const detalhamento = [];
    let total = 0;

    // Agora calcular impostos sobre os totais agregados
    tiposImposto.forEach(tipo => {
      try {
        switch (tipo) {
          case 'municipais':
            if (impostos.impostosMunicipais) {
              const percIRPecas = impostos.impostosMunicipais.pecas?.ir || 0;
              const irMunPecas = Math.round(totalValorPecas * percIRPecas) / 100;
              
              if (irMunPecas > 0) {
                total += irMunPecas;
                detalhamento.push({
                  titulo: `IR Municipal (${percIRPecas}% Peças)`,
                  valor: irMunPecas,
                  subitens: [
                    { texto: `Peças: ${formatarValor(totalValorPecas)} × ${percIRPecas}% = ${formatarValor(irMunPecas)}`, valor: irMunPecas }
                  ]
                });
              }
            }
            break;
          case 'estaduais':
            if (impostos.impostosEstaduais) {
              const estaduaisPecas = impostos.impostosEstaduais.pecas || {};
              const percTotalPecas = (estaduaisPecas.ir || 0) + (estaduaisPecas.pis || 0) + (estaduaisPecas.cofins || 0) + (estaduaisPecas.csll || 0);
              const totalEstPecas = Math.round(totalValorPecas * percTotalPecas) / 100;
              
              if (totalEstPecas > 0) {
                total += totalEstPecas;
                detalhamento.push({
                  titulo: `Impostos Estaduais (${formatarPercentual(percTotalPecas)}% Peças)`,
                  valor: totalEstPecas,
                  subitens: [
                    { texto: `Peças: ${formatarValor(totalValorPecas)} × ${formatarPercentual(percTotalPecas)}% = ${formatarValor(totalEstPecas)}`, valor: totalEstPecas }
                  ]
                });
              }
            }
            break;
          case 'federais':
            if (impostos.impostosFederais) {
              const federaisPecas = impostos.impostosFederais.pecas || {};
              const percTotalPecas = (federaisPecas.ir || 0) + (federaisPecas.pis || 0) + (federaisPecas.cofins || 0) + (federaisPecas.csll || 0);
              const totalFedPecas = Math.round(totalValorPecas * percTotalPecas) / 100;
              
              if (totalFedPecas > 0) {
                total += totalFedPecas;
                detalhamento.push({
                  titulo: `Impostos Federais (${formatarPercentual(percTotalPecas)}% Peças)`,
                  valor: totalFedPecas,
                  subitens: [
                    { texto: `Peças: ${formatarValor(totalValorPecas)} × ${formatarPercentual(percTotalPecas)}% = ${formatarValor(totalFedPecas)}`, valor: totalFedPecas }
                  ]
                });
              }
            }
            break;
          case 'retencoes':
            if (impostos.retencoesOrgao) {
              const percRetencoes = impostos.retencoesOrgao.percentual || 0;
              const retencoes = Math.round(totalValorPecas * percRetencoes) / 100;
              
              if (retencoes > 0) {
                total += retencoes;
                detalhamento.push({
                  titulo: `Retenções Órgão (${percRetencoes}%)`,
                  valor: retencoes,
                  subitens: [
                    { texto: `Peças: ${formatarValor(totalValorPecas)} × ${percRetencoes}% = ${formatarValor(retencoes)}`, valor: retencoes }
                  ]
                });
              }
            }
            break;
            default:
              break;
          }
        } catch (error) {
          console.error(`Erro ao calcular imposto ${tipo}:`, error);
        }
      });

    return { total, detalhamento };
  };

  const calcularDetalhamentoImpostosServicos = () => {
    if (!impostos || !fatura) return { total: 0, detalhamento: [] };

    // Primeiro, agregar todos os valores de serviços
    let totalValorServico = 0;
    let temFornecedorNaoOptante = false;
    let tiposImposto = new Set();

    ordensServico.forEach(item => {
      const ordem = item.ordemServico;
      const cliente = ordem.cliente;
      const fornecedor = ordem.fornecedor;

      if (fornecedor?.naoOptanteSimples) {
        temFornecedorNaoOptante = true;
        totalValorServico += ordem.valorServicoComDesconto || 0;
        
        if (cliente?.tipoImposto && Array.isArray(cliente.tipoImposto)) {
          cliente.tipoImposto.forEach(tipo => tiposImposto.add(tipo));
        }
      }
    });

    if (!temFornecedorNaoOptante || tiposImposto.size === 0) {
      return { total: 0, detalhamento: [] };
    }

    const detalhamento = [];
    let total = 0;

    // Agora calcular impostos sobre os totais agregados
    tiposImposto.forEach(tipo => {
      try {
        switch (tipo) {
          case 'municipais':
            if (impostos.impostosMunicipais) {
              const percIRServico = impostos.impostosMunicipais.servicos?.ir || 0;
              const irMunServico = Math.round(totalValorServico * percIRServico) / 100;
              
              if (irMunServico > 0) {
                total += irMunServico;
                detalhamento.push({
                  titulo: `IR Municipal (${percIRServico}% Serviços)`,
                  valor: irMunServico,
                  subitens: [
                    { texto: `Serviços: ${formatarValor(totalValorServico)} × ${percIRServico}% = ${formatarValor(irMunServico)}`, valor: irMunServico }
                  ]
                });
              }
            }
            break;
          case 'estaduais':
            if (impostos.impostosEstaduais) {
              const estaduaisServico = impostos.impostosEstaduais.servicos || {};
              const percTotalServico = (estaduaisServico.ir || 0) + (estaduaisServico.pis || 0) + (estaduaisServico.cofins || 0) + (estaduaisServico.csll || 0);
              const totalEstServico = Math.round(totalValorServico * percTotalServico) / 100;
              
              if (totalEstServico > 0) {
                total += totalEstServico;
                detalhamento.push({
                  titulo: `Impostos Estaduais (${formatarPercentual(percTotalServico)}% Serviços)`,
                  valor: totalEstServico,
                  subitens: [
                    { texto: `Serviços: ${formatarValor(totalValorServico)} × ${formatarPercentual(percTotalServico)}% = ${formatarValor(totalEstServico)}`, valor: totalEstServico }
                  ]
                });
              }
            }
            break;
          case 'federais':
            if (impostos.impostosFederais) {
              const federaisServico = impostos.impostosFederais.servicos || {};
              const percTotalServico = (federaisServico.ir || 0) + (federaisServico.pis || 0) + (federaisServico.cofins || 0) + (federaisServico.csll || 0);
              const totalFedServico = Math.round(totalValorServico * percTotalServico) / 100;
              
              if (totalFedServico > 0) {
                total += totalFedServico;
                detalhamento.push({
                  titulo: `Impostos Federais (${formatarPercentual(percTotalServico)}% Serviços)`,
                  valor: totalFedServico,
                  subitens: [
                    { texto: `Serviços: ${formatarValor(totalValorServico)} × ${formatarPercentual(percTotalServico)}% = ${formatarValor(totalFedServico)}`, valor: totalFedServico }
                  ]
                });
              }
            }
            break;
          case 'retencoes':
            if (impostos.retencoesOrgao) {
              const percRetencoes = impostos.retencoesOrgao.percentual || 0;
              const retencoes = Math.round(totalValorServico * percRetencoes) / 100;
              
              if (retencoes > 0) {
                total += retencoes;
                detalhamento.push({
                  titulo: `Retenções Órgão (${percRetencoes}%)`,
                  valor: retencoes,
                  subitens: [
                    { texto: `Serviços: ${formatarValor(totalValorServico)} × ${percRetencoes}% = ${formatarValor(retencoes)}`, valor: retencoes }
                  ]
                });
              }
            }
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(`Erro ao calcular imposto ${tipo}:`, error);
      }
    });

    return { total, detalhamento };
  };

  // Helper: normalizar dados da entidade (fornecedor tem campos flat, cliente pode ter nested)
  const getEntidadeDados = (ent) => {
    if (!ent) return { cnpj: '-', inscMunicipal: '-', inscEstadual: '-', endereco: '-', bairro: '-', cidade: '-', estado: '-', cep: '-', telefone: '-', email: '-' };
    return {
      cnpj: ent.cnpjCpf || ent.cnpj || '-',
      inscMunicipal: ent.inscricoes?.inscricaoMunicipal || ent.inscricaoMunicipal || '-',
      inscEstadual: ent.inscricoes?.inscricaoEstadual || ent.inscricaoEstadual || '-',
      endereco: ent.endereco?.logradouro 
        ? `${ent.endereco.logradouro}, ${ent.endereco.numero || 's/n'}${ent.endereco.complemento ? ', ' + ent.endereco.complemento : ''}`
        : (ent.endereco || '-'),
      bairro: ent.endereco?.bairro || ent.bairro || '-',
      cidade: ent.endereco?.cidade || ent.cidade || '-',
      estado: ent.endereco?.estado || ent.estado || '-',
      cep: ent.endereco?.cep || ent.cep || '-',
      telefone: ent.contatos?.telefone || ent.telefone || '-',
      email: ent.contatos?.email || ent.email || '-'
    };
  };

  // Helper: verificar se fatura é de abastecimento
  const isAbastecimento = fatura?.origem === 'abastecimento';
  const abastecimentosVinculados = fatura?.abastecimentosVinculados || [];

  const imprimirCompleta = () => {
    if (!fatura) return;

    const doc = new jsPDF('p', 'mm', 'a4');
    const entidade = fatura.tipo === 'Fornecedor' ? fatura.fornecedor : fatura.cliente;

    // Cabeçalho - InstaSolutions
    doc.setFontSize(16);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text('InstaSolutions Produtos e Gestão Empresarial LTDA', 105, 15, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0);
    doc.text('CNPJ: 47.611.398/0001-66', 105, 21, { align: 'center' });
    doc.text('Telefone: (11) 5118-3784 | WhatsApp: (11) 3336-6941', 105, 26, { align: 'center' });
    doc.text('Alameda Rio Negro, N° 1030, Escritório 2304 - Alphaville Industrial, Barueri - SP', 105, 31, { align: 'center' });
    
    // Linha separadora
    doc.setDrawColor(37, 28, 89);
    doc.setLineWidth(0.5);
    doc.line(20, 34, 190, 34);

    // Título
    doc.setFontSize(14);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    const tituloFatura = isAbastecimento ? 'FATURA DE ABASTECIMENTOS - COMPLETA' : 'FATURA DE SERVIÇOS - COMPLETA';
    doc.text(tituloFatura, 105, 42, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    doc.text(`Fatura Nº: ${fatura.numeroFatura}`, 20, 49);
    doc.text(`Data: ${formatarData(fatura.createdAt)}`, 190, 49, { align: 'right' });

    // Dados do Cliente/Fornecedor - COMPLETOS
    doc.setFontSize(11);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text(`Dados do ${fatura.tipo === 'Fornecedor' ? 'Fornecedor' : 'Cliente'}`, 20, 57);
    
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    let yPos = 63;
    const dados = getEntidadeDados(entidade);
    doc.text(`Razão Social: ${entidade?.razaoSocial || '-'}`, 20, yPos);
    yPos += 4;
    doc.text(`Nome Fantasia: ${entidade?.nomeFantasia || '-'}`, 20, yPos);
    yPos += 4;
    doc.text(`CNPJ: ${dados.cnpj}`, 20, yPos);
    yPos += 4;
    doc.text(`Insc. Municipal: ${dados.inscMunicipal}  |  Insc. Estadual: ${dados.inscEstadual}`, 20, yPos);
    yPos += 4;
    doc.text(`Endereço: ${dados.endereco}`, 20, yPos);
    yPos += 4;
    doc.text(`Bairro: ${dados.bairro}`, 20, yPos);
    yPos += 4;
    doc.text(`Cidade/UF: ${dados.cidade}/${dados.estado}  -  CEP: ${dados.cep}`, 20, yPos);
    yPos += 4;
    doc.text(`Telefone: ${dados.telefone}  |  E-mail: ${dados.email}`, 20, yPos);
    yPos += 6;

    // Dados Bancários (lado direito, alinhado com dados da entidade)
    if (fatura.tipo === 'Fornecedor' && entidade) {
      doc.setFontSize(11);
      doc.setTextColor(37, 28, 89);
      doc.setFont(undefined, 'bold');
      doc.text('Dados Bancários', 110, 57);
      doc.setFont(undefined, 'normal');
      
      doc.setFontSize(9);
      doc.setTextColor(0);
      let yBanco = 63;
      doc.text(`Banco: ${entidade?.banco || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Agência: ${entidade?.agencia || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Conta: ${entidade?.conta || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Tipo: ${entidade?.tipoConta || '-'}`, 110, yBanco);
      if (entidade?.chavePix) {
        yBanco += 4;
        doc.text(`Chave Pix: ${entidade.chavePix}`, 110, yBanco);
      }
    }

    // Tabela - OS ou Abastecimentos dependendo da origem
    let tableData, tableHead;
    if (isAbastecimento && abastecimentosVinculados.length > 0) {
      tableHead = [['Data', 'Cliente', 'Veículo', 'Motorista', 'Combustível', 'Litros', 'Vlr Bruto', 'Desc%', 'Vlr c/Desc', 'CC']];
      const TIPOS_COMB = { alcool: 'Álcool', gasolina_comum: 'Gas. Comum', gasolina_aditivada: 'Gas. Adit.', gnv: 'GNV', diesel: 'Diesel', diesel_500: 'Diesel S-500', diesel_s10: 'Diesel S-10', arla: 'ARLA', querosene: 'Querosene', glp: 'GLP' };
      tableData = abastecimentosVinculados.map(item => {
        const ab = item.abastecimento || {};
        return [
          ab.dataReferencia ? new Date(ab.dataReferencia).toLocaleDateString('pt-BR') : '-',
          ab.cliente?.razaoSocial || ab.cliente?.nomeFantasia || '-',
          ab.placa || '-',
          ab.motorista || '-',
          TIPOS_COMB[ab.tipoCombustivel] || ab.tipoCombustivel || '-',
          (ab.litrosAbastecidos || 0).toFixed(2),
          formatarValor(ab.valor || 0),
          `${(ab.descontoPercentual || 0).toFixed(1)}%`,
          formatarValor(ab.valorComDesconto || ab.valor || 0),
          ab.centroCusto || '-'
        ];
      });
    } else {
      tableHead = [[
        'Nº OS', 
        fatura.tipo === 'Fornecedor' ? 'Cliente' : 'Fornecedor', 
        'Veículo',
        'NF',
        'Vlr Peças', 
        'Desc%', 
        'Vlr c/Desc', 
        'Vlr Serv.', 
        'Desc%', 
        'Vlr c/Desc', 
        'Total'
      ]];
    tableData = ordensServico.map(item => {
      const os = item.ordemServico;
      const entityName = fatura.tipo === 'Fornecedor' 
        ? (os.cliente?.razaoSocial || os.cliente?.nomeFantasia || '-')
        : (os.fornecedor?.razaoSocial || os.fornecedor?.nomeFantasia || '-');
      
      const nfPeca = os.notaFiscalPeca || '-';
      const nfServico = os.notaFiscalServico || '-';
      const nfs = nfPeca !== '-' && nfServico !== '-' && nfPeca !== nfServico 
        ? `P:${nfPeca} S:${nfServico}` 
        : (nfPeca !== '-' ? nfPeca : nfServico);
      
      return [
        os.numeroOrdemServico || '-',
        entityName,
        os.placa || '-',
        nfs,
        formatarValor(os.valorPecas || 0),
        `${formatarPercentual(os.descontoPecasPerc || 0)}%`,
        formatarValor(os.valorPecasComDesconto || 0),
        formatarValor(os.valorServico || 0),
        `${formatarPercentual(os.descontoServicoPerc || 0)}%`,
        formatarValor(os.valorServicoComDesconto || 0),
        formatarValor(os.valorFinal || 0)
      ];
    });
    } // end else (OS)

    const tableColumnStyles = isAbastecimento
      ? { 0: { cellWidth: 16 }, 1: { cellWidth: 25 }, 2: { cellWidth: 16 }, 3: { cellWidth: 22 }, 4: { cellWidth: 18 }, 5: { cellWidth: 14 }, 6: { cellWidth: 18 }, 7: { cellWidth: 10 }, 8: { cellWidth: 19 }, 9: { cellWidth: 14 } }
      : { 0: { cellWidth: 16 }, 1: { cellWidth: 28 }, 2: { cellWidth: 12 }, 3: { cellWidth: 14 }, 4: { cellWidth: 18 }, 5: { cellWidth: 10 }, 6: { cellWidth: 19 }, 7: { cellWidth: 18 }, 8: { cellWidth: 10 }, 9: { cellWidth: 19 }, 10: { cellWidth: 20 } };

    autoTable(doc, {
      startY: yPos,
      head: tableHead,
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [37, 28, 89], fontSize: 7, fontStyle: 'bold' },
      styles: { fontSize: 6.5, cellPadding: 2, overflow: 'linebreak' },
      columnStyles: tableColumnStyles
    });

    /*
     * ═══ PROPOSTAS CONJUNTAS: UM BLOCO À PARTE, E NÃO UMA COLUNA A MAIS ═══
     *
     * A tabela acima é o formato que o cliente já recebe há tempo. Mexer nas
     * colunas dela mudaria TODO documento, inclusive os que não têm proposta
     * conjunta nenhuma — e o formato de faturamento é para preservar.
     *
     * Então o detalhamento vem depois, e SÓ quando há o que detalhar. Quem lê
     * uma fatura sem conjunto vê exatamente o documento de sempre.
     *
     * ⚠ O total do conjunto NÃO SOMA com as linhas: é a soma delas. O aviso
     * está impresso porque quem confere uma fatura com duas notas de CNPJs
     * diferentes vai querer somar tudo o que vê.
     */
    if (!isAbastecimento) {
      const conjuntos = new Map();
      ordensServico.forEach((item) => {
        const os = item.ordemServico;
        if (!os?.grupoPropostaId) return;
        if (!conjuntos.has(os.grupoPropostaId)) conjuntos.set(os.grupoPropostaId, []);
        conjuntos.get(os.grupoPropostaId).push(item);
      });

      if (conjuntos.size > 0) {
        let yConj = doc.lastAutoTable.finalY + 8;
        if (yConj > 240) { doc.addPage(); yConj = 20; }

        doc.setFontSize(10);
        doc.setTextColor(37, 28, 89);
        doc.setFont(undefined, 'bold');
        doc.text('PROPOSTAS CONJUNTAS — PARCELAS POR CNPJ', 20, yConj);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(7);
        doc.setTextColor(90, 90, 90);
        doc.text(
          'Cada parcela e uma obrigacao propria, com nota e retencao do seu emitente. O total do conjunto e a soma das parcelas: nao some os dois.',
          20, yConj + 4, { maxWidth: 170 }
        );

        const linhasConj = [];
        conjuntos.forEach((itens, grupoId) => {
          itens.forEach((item) => {
            const os = item.ordemServico;
            linhasConj.push([
              `#${grupoId}`,
              os.parcelaPapel === 'pecas' ? 'Pecas' : os.parcelaPapel === 'servicos' ? 'Servicos' : '-',
              os.fornecedor?.razaoSocial || os.fornecedor?.nomeFantasia || '-',
              os.fornecedor?.cnpjCpf || '-',
              os.notaFiscalPeca || os.notaFiscalServico || '-',
              formatarValor(item.valorBruto ?? os.valorFinal ?? 0),
              item.valorRetencao != null ? formatarValor(item.valorRetencao) : 'nao apurada',
              item.valorLiquido != null ? formatarValor(item.valorLiquido) : 'nao apurado',
            ]);
          });

          // O cabeçalho do conjunto entra como uma linha PRÓPRIA e marcada.
          const primeiro = itens[0].ordemServico;
          linhasConj.push([
            `#${grupoId}`,
            'TOTAL',
            'Total do conjunto (nao somavel com as linhas acima)',
            '',
            '',
            primeiro.grupoTotalConsolidado != null
              ? formatarValor(primeiro.grupoTotalConsolidado)
              : formatarValor(itens.reduce((s, i) => s + (i.valorBruto ?? 0), 0)),
            '',
            '',
          ]);
        });

        autoTable(doc, {
          startY: yConj + 10,
          head: [['Conjunto', 'Parcela', 'Emitente', 'CNPJ', 'NF', 'Bruto', 'Retencao', 'Liquido']],
          body: linhasConj,
          theme: 'grid',
          headStyles: { fillColor: [79, 70, 229], fontSize: 7, fontStyle: 'bold' },
          styles: { fontSize: 6.5, cellPadding: 2, overflow: 'linebreak' },
          columnStyles: {
            0: { cellWidth: 16 }, 1: { cellWidth: 16 }, 2: { cellWidth: 40 }, 3: { cellWidth: 26 },
            4: { cellWidth: 18 }, 5: { cellWidth: 20 }, 6: { cellWidth: 20 }, 7: { cellWidth: 20 },
          },
          // A linha de total do conjunto fica cinza para ninguém somá-la junto.
          didParseCell: (d) => {
            if (d.section === 'body' && d.row.raw[1] === 'TOTAL') {
              d.cell.styles.fillColor = [226, 232, 240];
              d.cell.styles.fontStyle = 'bold';
            }
          },
        });
      }
    }

    // Resumo Financeiro com fórmula corrigida
    const afterTableY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text('RESUMO FINANCEIRO', 20, afterTableY);
    
    // Para Faturas Fornecedor: sempre considerar impostos se houver
    // Para Faturas Cliente: apenas se houver fornecedor não optante
    let valorImpostosExibir = 0;
    
    if (fatura.tipo === 'Fornecedor') {
      valorImpostosExibir = fatura.valorImpostos || 0;
    } else if (fatura.tipo === 'Cliente') {
      const temNaoOptante = ordensServico.some(item => 
        item.ordemServico.fornecedor?.naoOptanteSimples === true
      );
      valorImpostosExibir = temNaoOptante ? (fatura.valorImpostos || 0) : 0;
    }
    
    // Calcular valores separados por peças e serviços (apenas para OS)
    let valorTotalPecas = 0;
    let valorTotalServicos = 0;
    
    if (!isAbastecimento) {
      ordensServico.forEach(item => {
        valorTotalPecas += item.ordemServico.valorPecas || 0;
        valorTotalServicos += item.ordemServico.valorServico || 0;
      });
    }
    
    // Calcular detalhamento de impostos para determinar altura do box
    const detalhamentoPreCalculo = calcularDetalhamentoImpostos();
    const totalLinhasDetalhamento = detalhamentoPreCalculo.detalhamento.reduce((acc, item) => {
      return acc + 1 + item.subitens.length;
    }, 0);
    
    const numImpostoItems = detalhamentoPreCalculo.detalhamento.length;
    // Abastecimento: sem linhas peças/serviços, com linhas litros/bruto
    let alturaBox = isAbastecimento ? 49 : 49;
    if (detalhamentoPreCalculo.total > 0) {
      const detalheAltura = numImpostoItems * 5 + (totalLinhasDetalhamento - numImpostoItems) * 3.5;
      alturaBox = 65 + detalheAltura;
    }
    
    let finalY = afterTableY + 5;
    
    // Verificar se o resumo cabe na página atual
    if (finalY + alturaBox + 20 > 275) {
      doc.addPage();
      finalY = 20;
    }
    
    doc.setFillColor(248, 249, 250);
    doc.rect(20, finalY, 170, alturaBox, 'F');
    doc.setDrawColor(224, 224, 224);
    doc.rect(20, finalY, 170, alturaBox);
    
    finalY += 6;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    
    if (isAbastecimento) {
      // Abastecimentos: Valor Bruto
      doc.text('Valor Bruto (Total Abastecimentos):', 25, finalY);
      doc.text(formatarValor(fatura.valorTotal || 0), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
    } else {
      // OS: Valor Total Peças e Serviços
      doc.text('Valor Total Peças:', 25, finalY);
      doc.text(formatarValor(valorTotalPecas), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
      
      doc.text('Valor Total Serviços:', 25, finalY);
      doc.text(formatarValor(valorTotalServicos), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
    }
    
    // Desconto Contrato
    doc.text('(-) Desconto Contrato:', 25, finalY);
    doc.text(formatarValor(fatura.valorDesconto || 0), 185, finalY, { align: 'right' });
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 7;
    
    // Valor com Desconto - Destacado
    doc.setFillColor(227, 242, 253);
    doc.rect(20, finalY - 4, 170, 8, 'F');
    doc.setFont(undefined, 'bold');
    doc.setFontSize(10);
    doc.text('Valor com Desconto:', 25, finalY);
    doc.text(formatarValor(fatura.valorComDesconto || 0), 185, finalY, { align: 'right' });
    doc.setDrawColor(0, 91, 237);
    doc.setLineWidth(1);
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 10;
    
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.5);
    
    // Calcular detalhamento de impostos
    const detalhamentoImpostos = calcularDetalhamentoImpostos();
    const valorImpostosCalculado = detalhamentoImpostos.total || valorImpostosExibir;
    
    // Impostos & Retenções com detalhamento
    const valorAposImpostos = (fatura.valorComDesconto || 0) - valorImpostosCalculado;
    
    if (valorImpostosCalculado > 0) {
      doc.text('(-) Impostos & Retenções:', 25, finalY);
      doc.text(formatarValor(valorImpostosCalculado), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
      
      // Detalhamento dos impostos
      if (detalhamentoImpostos.detalhamento.length > 0) {
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        detalhamentoImpostos.detalhamento.forEach(item => {
          doc.text(`  ${item.titulo}: ${formatarValor(item.valor)}`, 30, finalY);
          finalY += 4;
          
          item.subitens.forEach(sub => {
            doc.text(`    • ${sub.texto}`, 35, finalY);
            finalY += 3.5;
          });
          finalY += 1;
        });
        doc.setFontSize(9);
        doc.setTextColor(0);
        finalY += 2;
      }
      
      // Valor após Impostos & Retenções
      doc.setFont(undefined, 'bold');
      doc.text('Valor após Impostos & Retenções:', 25, finalY);
      doc.text(formatarValor(valorAposImpostos), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
      doc.setFont(undefined, 'normal');
    }
    
    // Taxa de Operação - Recalcular sobre a base correta (valor após impostos)
    let valorTaxaOperacaoCorreto = fatura.valorTaxasOperacao || 0;
    if (percentualTaxaOperacao !== null && valorAposImpostos > 0) {
      valorTaxaOperacaoCorreto = Math.round(valorAposImpostos * percentualTaxaOperacao / 100 * 100) / 100;
    }
    
    doc.text(`${getTextoTaxaOperacao()}:`, 25, finalY);
    doc.text(formatarValor(valorTaxaOperacaoCorreto), 185, finalY, { align: 'right' });
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 10;
    
    // Valor Devido - Final destacado (fórmula: Valor após Impostos - Taxa Operação)
    const valorDevidoCorreto = valorAposImpostos - valorTaxaOperacaoCorreto;
    if (finalY + 15 > 280) {
      doc.addPage();
      finalY = 20;
    }
    doc.setFillColor(0, 91, 237);
    doc.rect(20, finalY, 170, 10, 'F');
    doc.setDrawColor(0, 91, 237);
    doc.setLineWidth(1);
    doc.rect(20, finalY, 170, 10);
    
    finalY += 7;
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('VALOR DEVIDO:', 25, finalY);
    doc.text(formatarValor(valorDevidoCorreto), 185, finalY, { align: 'right' });

    // Texto informativo sobre retenções
    finalY += 12;
    if (finalY + 30 > 280) {
      doc.addPage();
      finalY = 20;
    }
    doc.setFontSize(7.5);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'italic');
    if (isAbastecimento) {
      doc.text('Informações sobre retenções tributárias de combustível: consulte o sistema de Combustível na página "Manuais".', 20, finalY);
    } else {
      doc.text('Informações sobre retenções tributárias de Manutenção (OS): consulte a página "Tributação e Precificação" para orientações sobre ressarcimento.', 20, finalY);
    }
    finalY += 4;
    doc.text('Em caso de dúvidas, entre em contato pelo telefone (11) 3336-6941.', 20, finalY);

    doc.save(`${fatura.numeroFatura}_completa.pdf`);
    toast.success('PDF completo gerado com sucesso!');
  };

  const imprimirPecas = () => {
    if (!fatura) return;

    const doc = new jsPDF('p', 'mm', 'a4');
    const entidade = fatura.tipo === 'Fornecedor' ? fatura.fornecedor : fatura.cliente;

    // Cabeçalho - InstaSolutions
    doc.setFontSize(16);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text('InstaSolutions Produtos e Gestão Empresarial LTDA', 105, 15, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0);
    doc.text('CNPJ: 47.611.398/0001-66', 105, 21, { align: 'center' });
    doc.text('Telefone: (11) 5118-3784 | WhatsApp: (11) 3336-6941', 105, 26, { align: 'center' });
    doc.text('Alameda Rio Negro, N° 1030, Escritório 2304 - Alphaville Industrial, Barueri - SP', 105, 31, { align: 'center' });
    
    // Linha separadora
    doc.setDrawColor(37, 28, 89);
    doc.setLineWidth(0.5);
    doc.line(20, 34, 190, 34);

    // Título
    doc.setFontSize(14);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text('FATURA DE SERVIÇOS - SOMENTE PEÇAS', 105, 42, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    doc.text(`Fatura Nº: ${fatura.numeroFatura}`, 20, 49);
    doc.text(`Data: ${formatarData(fatura.createdAt)}`, 190, 49, { align: 'right' });

    // Dados do Cliente/Fornecedor - COMPLETOS
    doc.setFontSize(11);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text(`Dados do ${fatura.tipo === 'Fornecedor' ? 'Fornecedor' : 'Cliente'}`, 20, 57);
    
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    let yPos = 63;
    const dados = getEntidadeDados(entidade);
    doc.text(`Razão Social: ${entidade?.razaoSocial || '-'}`, 20, yPos);
    yPos += 4;
    doc.text(`Nome Fantasia: ${entidade?.nomeFantasia || '-'}`, 20, yPos);
    yPos += 4;
    doc.text(`CNPJ: ${dados.cnpj}`, 20, yPos);
    yPos += 4;
    doc.text(`Insc. Municipal: ${dados.inscMunicipal}  |  Insc. Estadual: ${dados.inscEstadual}`, 20, yPos);
    yPos += 4;
    doc.text(`Endereço: ${dados.endereco}`, 20, yPos);
    yPos += 4;
    doc.text(`Bairro: ${dados.bairro}`, 20, yPos);
    yPos += 4;
    doc.text(`Cidade/UF: ${dados.cidade}/${dados.estado}  -  CEP: ${dados.cep}`, 20, yPos);
    yPos += 4;
    doc.text(`Telefone: ${dados.telefone}  |  E-mail: ${dados.email}`, 20, yPos);
    yPos += 6;

    // Dados Bancários
    if (fatura.tipo === 'Fornecedor' && entidade) {
      doc.setFontSize(11);
      doc.setTextColor(37, 28, 89);
      doc.setFont(undefined, 'bold');
      doc.text('Dados Bancários', 110, 57);
      doc.setFont(undefined, 'normal');
      
      doc.setFontSize(9);
      doc.setTextColor(0);
      let yBanco = 63;
      doc.text(`Banco: ${entidade?.banco || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Agência: ${entidade?.agencia || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Conta: ${entidade?.conta || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Tipo: ${entidade?.tipoConta || '-'}`, 110, yBanco);
      if (entidade?.chavePix) {
        yBanco += 4;
        doc.text(`Chave Pix: ${entidade.chavePix}`, 110, yBanco);
      }
    }

    // Tabela de Ordens - Somente Peças (filtra OS com valor zerado)
    const tableData = ordensServico
      .filter(item => (item.ordemServico.valorPecasComDesconto || 0) > 0)
      .map(item => {
        const os = item.ordemServico;
        const entityName = fatura.tipo === 'Fornecedor' 
          ? (os.cliente?.razaoSocial || os.cliente?.nomeFantasia || '-')
          : (os.fornecedor?.razaoSocial || os.fornecedor?.nomeFantasia || '-');
        
        return [
          os.numeroOrdemServico || '-',
          entityName,
          os.placa || '-',
          os.notaFiscalPeca || '-',
          formatarValor(os.valorPecas || 0),
          `${formatarPercentual(os.descontoPecasPerc || 0)}%`,
          formatarValor(os.valorPecasComDesconto || 0)
        ];
      });

    autoTable(doc, {
      startY: yPos,
      head: [[
        'Nº OS', 
        fatura.tipo === 'Fornecedor' ? 'Cliente' : 'Fornecedor', 
        'Veículo',
        'Nota Fiscal',
        'Valor Peças', 
        'Desconto %', 
        'Valor c/ Desconto'
      ]],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [37, 28, 89], fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 22 }, 1: { cellWidth: 50 }, 2: { cellWidth: 18 },
        3: { cellWidth: 25 }, 4: { cellWidth: 20 }, 5: { cellWidth: 15 }, 6: { cellWidth: 24 }
      }
    });

    // Resumo Financeiro com cálculos proporcionais para PEÇAS
    const afterTableY = doc.lastAutoTable.finalY + 10;
    
    let finalY = afterTableY;
    
    const valorPecasTotal = ordensServico.reduce((sum, item) => sum + (item.ordemServico.valorPecas || 0), 0);
    const valorPecasDesconto = ordensServico.reduce((sum, item) => sum + (item.ordemServico.valorPecasComDesconto || 0), 0);
    const descontoTotal = valorPecasTotal - valorPecasDesconto;
    
    // Calcular proporção de peças em relação ao total
    const valorTotalFatura = fatura.valorComDesconto || 1;
    
    // Calcular impostos detalhados apenas para peças
    const detalhamentoImpostosPecas = calcularDetalhamentoImpostosPecas();
    const impostosPecas = detalhamentoImpostosPecas.total;
    
    const valorAposImpostos = valorPecasDesconto - impostosPecas;
    
    // CORREÇÃO: Taxa proporcional calculada sobre valor após impostos
    const proporcaoPecas = valorPecasDesconto / valorTotalFatura;
    let taxaOperacaoPecas;
    if (percentualTaxaOperacao !== null && valorAposImpostos > 0) {
      taxaOperacaoPecas = Math.round(valorAposImpostos * (percentualTaxaOperacao / 100) * 100) / 100;
    } else {
      taxaOperacaoPecas = (fatura.valorTaxasOperacao || 0) * proporcaoPecas;
    }
    const valorDevidoPecas = valorAposImpostos - taxaOperacaoPecas;
    
    // Calcular altura do box dinamicamente
    const totalLinhasDetalhamento = detalhamentoImpostosPecas.detalhamento.reduce((acc, item) => {
      return acc + 1 + item.subitens.length;
    }, 0);
    const numImpostoItems = detalhamentoImpostosPecas.detalhamento.length;
    // Base sem impostos: padding(6) + peças(7) + desconto(7) + destaque(10) + taxa(10) + margem(2) = 42
    const alturaBox = impostosPecas > 0 
      ? (58 + numImpostoItems * 5 + (totalLinhasDetalhamento - numImpostoItems) * 3.5) 
      : 42;
    
    // Verificar se cabe na página (título + box + valor devido)
    if (finalY + alturaBox + 25 > 275) {
      doc.addPage();
      finalY = 20;
    }
    
    // Título RESUMO FINANCEIRO (após verificação de página para não ficar órfão)
    doc.setFontSize(11);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text('RESUMO FINANCEIRO', 20, finalY);
    finalY += 5;
    
    doc.setFillColor(248, 249, 250);
    doc.rect(20, finalY, 170, alturaBox, 'F');
    doc.setDrawColor(224, 224, 224);
    doc.rect(20, finalY, 170, alturaBox);
    
    finalY += 6;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    
    doc.text('Valor Total Peças:', 25, finalY);
    doc.text(formatarValor(valorPecasTotal), 185, finalY, { align: 'right' });
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 7;
    
    doc.text('(-) Desconto Contrato:', 25, finalY);
    doc.text(formatarValor(descontoTotal), 185, finalY, { align: 'right' });
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 7;
    
    doc.setFillColor(227, 242, 253);
    doc.rect(20, finalY - 4, 170, 8, 'F');
    doc.setFont(undefined, 'bold');
    doc.text('Valor com Desconto:', 25, finalY);
    doc.text(formatarValor(valorPecasDesconto), 185, finalY, { align: 'right' });
    doc.setDrawColor(0, 91, 237);
    doc.setLineWidth(1);
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 10;
    
    doc.setFont(undefined, 'normal');
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.5);
    
    if (impostosPecas > 0) {
      doc.text('(-) Impostos & Retenções:', 25, finalY);
      doc.text(formatarValor(impostosPecas), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
      
      // Detalhamento dos impostos
      if (detalhamentoImpostosPecas.detalhamento.length > 0) {
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        detalhamentoImpostosPecas.detalhamento.forEach(item => {
          doc.text(`  ${item.titulo}: ${formatarValor(item.valor)}`, 30, finalY);
          finalY += 4;
          
          item.subitens.forEach(sub => {
            doc.text(`    • ${sub.texto}`, 35, finalY);
            finalY += 3.5;
          });
          finalY += 1;
        });
        doc.setFontSize(9);
        doc.setTextColor(0);
        finalY += 2;
      }
      
      // Valor após Impostos & Retenções
      doc.setFont(undefined, 'bold');
      doc.text('Valor após Impostos & Retenções:', 25, finalY);
      doc.text(formatarValor(valorAposImpostos), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
      doc.setFont(undefined, 'normal');
    }
    
    doc.text(`${getTextoTaxaOperacao()}:`, 25, finalY);
    doc.text(formatarValor(taxaOperacaoPecas), 185, finalY, { align: 'right' });
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 10;
    
    if (finalY + 15 > 280) {
      doc.addPage();
      finalY = 20;
    }
    doc.setFillColor(0, 91, 237);
    doc.rect(20, finalY, 170, 10, 'F');
    finalY += 7;
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('VALOR DEVIDO:', 25, finalY);
    doc.text(formatarValor(valorDevidoPecas), 185, finalY, { align: 'right' });

    // Texto informativo sobre retenções
    finalY += 10;
    if (finalY + 20 > 280) {
      doc.addPage();
      finalY = 20;
    }
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'italic');
    doc.text('* As retenções de impostos aplicadas nesta fatura seguem as regras de tributação configuradas para cada cliente/órgão.', 20, finalY);
    finalY += 4;
    doc.text('Para mais informações sobre as regras de retenção aplicáveis, consulte a página "Tributação e Precificação" no sistema.', 20, finalY);

    doc.save(`${fatura.numeroFatura}_pecas.pdf`);
    toast.success('PDF de peças gerado com sucesso!');
  };

  const imprimirServicos = () => {
    if (!fatura) return;

    const doc = new jsPDF('p', 'mm', 'a4');
    const entidade = fatura.tipo === 'Fornecedor' ? fatura.fornecedor : fatura.cliente;

    // Cabeçalho - InstaSolutions
    doc.setFontSize(16);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text('InstaSolutions Produtos e Gestão Empresarial LTDA', 105, 15, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0);
    doc.text('CNPJ: 47.611.398/0001-66', 105, 21, { align: 'center' });
    doc.text('Telefone: (11) 5118-3784 | WhatsApp: (11) 3336-6941', 105, 26, { align: 'center' });
    doc.text('Alameda Rio Negro, N° 1030, Escritório 2304 - Alphaville Industrial, Barueri - SP', 105, 31, { align: 'center' });
    
    // Linha separadora
    doc.setDrawColor(37, 28, 89);
    doc.setLineWidth(0.5);
    doc.line(20, 34, 190, 34);

    // Título
    doc.setFontSize(14);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text('FATURA DE SERVIÇOS - SOMENTE SERVIÇOS', 105, 42, { align: 'center' });
    
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    doc.text(`Fatura Nº: ${fatura.numeroFatura}`, 20, 49);
    doc.text(`Data: ${formatarData(fatura.createdAt)}`, 190, 49, { align: 'right' });

    // Dados do Cliente/Fornecedor - COMPLETOS
    doc.setFontSize(11);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text(`Dados do ${fatura.tipo === 'Fornecedor' ? 'Fornecedor' : 'Cliente'}`, 20, 57);
    
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    let yPos = 63;
    const dados = getEntidadeDados(entidade);
    doc.text(`Razão Social: ${entidade?.razaoSocial || '-'}`, 20, yPos);
    yPos += 4;
    doc.text(`Nome Fantasia: ${entidade?.nomeFantasia || '-'}`, 20, yPos);
    yPos += 4;
    doc.text(`CNPJ: ${dados.cnpj}`, 20, yPos);
    yPos += 4;
    doc.text(`Insc. Municipal: ${dados.inscMunicipal}  |  Insc. Estadual: ${dados.inscEstadual}`, 20, yPos);
    yPos += 4;
    doc.text(`Endereço: ${dados.endereco}`, 20, yPos);
    yPos += 4;
    doc.text(`Bairro: ${dados.bairro}`, 20, yPos);
    yPos += 4;
    doc.text(`Cidade/UF: ${dados.cidade}/${dados.estado}  -  CEP: ${dados.cep}`, 20, yPos);
    yPos += 4;
    doc.text(`Telefone: ${dados.telefone}  |  E-mail: ${dados.email}`, 20, yPos);
    yPos += 6;

    // Dados Bancários
    if (fatura.tipo === 'Fornecedor' && entidade) {
      doc.setFontSize(11);
      doc.setTextColor(37, 28, 89);
      doc.setFont(undefined, 'bold');
      doc.text('Dados Bancários', 110, 57);
      doc.setFont(undefined, 'normal');
      
      doc.setFontSize(9);
      doc.setTextColor(0);
      let yBanco = 63;
      doc.text(`Banco: ${entidade?.banco || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Agência: ${entidade?.agencia || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Conta: ${entidade?.conta || '-'}`, 110, yBanco);
      yBanco += 4;
      doc.text(`Tipo: ${entidade?.tipoConta || '-'}`, 110, yBanco);
      if (entidade?.chavePix) {
        yBanco += 4;
        doc.text(`Chave Pix: ${entidade.chavePix}`, 110, yBanco);
      }
    }

    // Tabela de Ordens - Somente Serviços (filtra OS com valor zerado)
    const tableData = ordensServico
      .filter(item => (item.ordemServico.valorServicoComDesconto || 0) > 0)
      .map(item => {
        const os = item.ordemServico;
        const entityName = fatura.tipo === 'Fornecedor' 
          ? (os.cliente?.razaoSocial || os.cliente?.nomeFantasia || '-')
          : (os.fornecedor?.razaoSocial || os.fornecedor?.nomeFantasia || '-');
        
        return [
          os.numeroOrdemServico || '-',
          entityName,
          os.placa || '-',
          os.notaFiscalServico || '-',
          formatarValor(os.valorServico || 0),
          `${formatarPercentual(os.descontoServicoPerc || 0)}%`,
          formatarValor(os.valorServicoComDesconto || 0)
        ];
      });

    autoTable(doc, {
      startY: yPos,
      head: [[
        'Nº OS', 
        fatura.tipo === 'Fornecedor' ? 'Cliente' : 'Fornecedor', 
        'Veículo',
        'Nota Fiscal',
        'Valor Serviços', 
        'Desconto %', 
        'Valor c/ Desconto'
      ]],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [37, 28, 89], fontSize: 9, fontStyle: 'bold' },
      styles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: {
        0: { cellWidth: 22 }, 1: { cellWidth: 50 }, 2: { cellWidth: 18 },
        3: { cellWidth: 25 }, 4: { cellWidth: 20 }, 5: { cellWidth: 15 }, 6: { cellWidth: 24 }
      }
    });

    // Resumo Financeiro com cálculos proporcionais para SERVIÇOS
    const afterTableY = doc.lastAutoTable.finalY + 10;
    
    let finalY = afterTableY;
    
    const valorServicosTotal = ordensServico.reduce((sum, item) => sum + (item.ordemServico.valorServico || 0), 0);
    const valorServicosDesconto = ordensServico.reduce((sum, item) => sum + (item.ordemServico.valorServicoComDesconto || 0), 0);
    const descontoTotal = valorServicosTotal - valorServicosDesconto;
    
    // Calcular proporção de serviços em relação ao total
    const valorTotalFatura = fatura.valorComDesconto || 1;
    
    // Calcular impostos detalhados apenas para serviços
    const detalhamentoImpostosServicos = calcularDetalhamentoImpostosServicos();
    const impostosServicos = detalhamentoImpostosServicos.total;
    
    const valorAposImpostos = valorServicosDesconto - impostosServicos;
    
    // CORREÇÃO: Taxa proporcional calculada sobre valor após impostos
    const proporcaoServicos = valorServicosDesconto / valorTotalFatura;
    let taxaOperacaoServicos;
    if (percentualTaxaOperacao !== null && valorAposImpostos > 0) {
      taxaOperacaoServicos = Math.round(valorAposImpostos * (percentualTaxaOperacao / 100) * 100) / 100;
    } else {
      taxaOperacaoServicos = (fatura.valorTaxasOperacao || 0) * proporcaoServicos;
    }
    const valorDevidoServicos = valorAposImpostos - taxaOperacaoServicos;
    
    // Calcular altura do box dinamicamente
    const totalLinhasDetalhamento = detalhamentoImpostosServicos.detalhamento.reduce((acc, item) => {
      return acc + 1 + item.subitens.length;
    }, 0);
    const numImpostoItems = detalhamentoImpostosServicos.detalhamento.length;
    // Base sem impostos: padding(6) + serviços(7) + desconto(7) + destaque(10) + taxa(10) + margem(2) = 42
    const alturaBox = impostosServicos > 0 
      ? (58 + numImpostoItems * 5 + (totalLinhasDetalhamento - numImpostoItems) * 3.5) 
      : 42;
    
    // Verificar se cabe na página (título + box + valor devido)
    if (finalY + alturaBox + 25 > 275) {
      doc.addPage();
      finalY = 20;
    }
    
    // Título RESUMO FINANCEIRO (após verificação de página para não ficar órfão)
    doc.setFontSize(11);
    doc.setTextColor(37, 28, 89);
    doc.setFont(undefined, 'bold');
    doc.text('RESUMO FINANCEIRO', 20, finalY);
    finalY += 5;
    
    doc.setFillColor(248, 249, 250);
    doc.rect(20, finalY, 170, alturaBox, 'F');
    doc.setDrawColor(224, 224, 224);
    doc.rect(20, finalY, 170, alturaBox);
    
    finalY += 6;
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.setFont(undefined, 'normal');
    
    doc.text('Valor Total Serviços:', 25, finalY);
    doc.text(formatarValor(valorServicosTotal), 185, finalY, { align: 'right' });
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 7;
    
    doc.text('(-) Desconto Contrato:', 25, finalY);
    doc.text(formatarValor(descontoTotal), 185, finalY, { align: 'right' });
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 7;
    
    doc.setFillColor(227, 242, 253);
    doc.rect(20, finalY - 4, 170, 8, 'F');
    doc.setFont(undefined, 'bold');
    doc.text('Valor com Desconto:', 25, finalY);
    doc.text(formatarValor(valorServicosDesconto), 185, finalY, { align: 'right' });
    doc.setDrawColor(0, 91, 237);
    doc.setLineWidth(1);
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 10;
    
    doc.setFont(undefined, 'normal');
    doc.setDrawColor(224, 224, 224);
    doc.setLineWidth(0.5);
    
    if (impostosServicos > 0) {
      doc.text('(-) Impostos & Retenções:', 25, finalY);
      doc.text(formatarValor(impostosServicos), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
      
      // Detalhamento dos impostos
      if (detalhamentoImpostosServicos.detalhamento.length > 0) {
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        detalhamentoImpostosServicos.detalhamento.forEach(item => {
          doc.text(`  ${item.titulo}: ${formatarValor(item.valor)}`, 30, finalY);
          finalY += 4;
          
          item.subitens.forEach(sub => {
            doc.text(`    • ${sub.texto}`, 35, finalY);
            finalY += 3.5;
          });
          finalY += 1;
        });
        doc.setFontSize(9);
        doc.setTextColor(0);
        finalY += 2;
      }
      
      // Valor após Impostos & Retenções
      doc.setFont(undefined, 'bold');
      doc.text('Valor após Impostos & Retenções:', 25, finalY);
      doc.text(formatarValor(valorAposImpostos), 185, finalY, { align: 'right' });
      doc.line(20, finalY + 2, 190, finalY + 2);
      finalY += 7;
      doc.setFont(undefined, 'normal');
    }
    
    doc.text(`${getTextoTaxaOperacao()}:`, 25, finalY);
    doc.text(formatarValor(taxaOperacaoServicos), 185, finalY, { align: 'right' });
    doc.line(20, finalY + 2, 190, finalY + 2);
    finalY += 10;
    
    if (finalY + 15 > 280) {
      doc.addPage();
      finalY = 20;
    }
    doc.setFillColor(0, 91, 237);
    doc.rect(20, finalY, 170, 10, 'F');
    finalY += 7;
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('VALOR DEVIDO:', 25, finalY);
    doc.text(formatarValor(valorDevidoServicos), 185, finalY, { align: 'right' });

    // Texto informativo sobre retenções
    finalY += 10;
    if (finalY + 20 > 280) {
      doc.addPage();
      finalY = 20;
    }
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'italic');
    doc.text('* As retenções de impostos aplicadas nesta fatura seguem as regras de tributação configuradas para cada cliente/órgão.', 20, finalY);
    finalY += 4;
    doc.text('Para mais informações sobre as regras de retenção aplicáveis, consulte a página "Tributação e Precificação" no sistema.', 20, finalY);

    doc.save(`${fatura.numeroFatura}_servicos.pdf`);
    toast.success('PDF de serviços gerado com sucesso!');
  };

  if (loading) {
    return (
      <div className="page-container">
        <Header user={user} />
        <div className="content-wrapper">
          <Sidebar user={user} />
          <main className="main-content">
            <div className="loading">Carregando fatura...</div>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  if (!fatura) {
    return null;
  }

  return (
    <div className="page-container">
      <Header user={user} />
      <div className="content-wrapper">
        <Sidebar user={user} />
        <main className="main-content">
          <div className="faturado-detalhes-container">
            {/* Cabeçalho */}
            <div className="page-header">
              <button className="btn-voltar" onClick={() => navigate('/faturados')}>
                ← Voltar
              </button>
              <div>
                <h1>Fatura {fatura.numeroFatura}</h1>
                <p>
                  {fatura.tipo === 'Fornecedor' 
                    ? fatura.fornecedor?.razaoSocial || fatura.fornecedor?.nomeFantasia
                    : fatura.cliente?.razaoSocial || fatura.cliente?.nomeFantasia
                  }
                </p>
              </div>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <span className={getStatusBadgeClass(fatura.statusFatura)}>
                  {fatura.statusFatura}
                </span>
                {!isReadOnly && (
                  <button 
                    className="btn-excluir-fatura" 
                    onClick={handleExcluirFatura}
                    title="Excluir fatura"
                  >
                    🗑️ Excluir
                  </button>
                )}
              </div>
            </div>

            {/* Informações da fatura */}
            <div className="fatura-info-card">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Período Apurado:</span>
                  <span className="info-value">
                    {formatarData(fatura.periodoInicio)} - {formatarData(fatura.periodoFim)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Data de Criação:</span>
                  <span className="info-value">{formatarData(fatura.createdAt)}</span>
                </div>
                
                {/* Campo de Previsão de Recebimento - apenas para faturas de fornecedor */}
                {fatura.tipo === 'Fornecedor' && (
                  <div className="info-item info-item-full">
                    <span className="info-label">Previsão de Recebimento:</span>
                    {editandoPrevisao && !isReadOnly ? (
                      <div className="previsao-edit-group">
                        <input
                          type="date"
                          value={previsaoRecebimento}
                          onChange={(e) => setPrevisaoRecebimento(e.target.value)}
                          className="input-previsao"
                        />
                        <button onClick={handleSalvarPrevisao} className="btn-salvar-mini">
                          ✓
                        </button>
                        <button onClick={handleCancelarPrevisao} className="btn-cancelar-mini">
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="previsao-display-group">
                        <span className="info-value">
                          {previsaoRecebimento ? formatarData(previsaoRecebimento) : 'Não definida'}
                        </span>
                        {!isReadOnly && (
                          <button 
                            onClick={() => setEditandoPrevisao(true)} 
                            className="btn-editar-mini"
                            title="Editar previsão"
                          >
                            ✏️
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Campo de Data de Vencimento - apenas para faturas de cliente */}
                {fatura.tipo === 'Cliente' && (
                  <div className="info-item info-item-full">
                    <span className="info-label">Data de Vencimento:</span>
                    {editandoVencimento && !isReadOnly ? (
                      <div className="previsao-edit-group">
                        <input
                          type="date"
                          value={dataVencimento}
                          onChange={(e) => setDataVencimento(e.target.value)}
                          className="input-previsao"
                        />
                        <button onClick={handleSalvarVencimento} className="btn-salvar-mini">
                          ✓
                        </button>
                        <button onClick={handleCancelarVencimento} className="btn-cancelar-mini">
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="previsao-display-group">
                        <span className="info-value">
                          {dataVencimento ? formatarData(dataVencimento) : 'Não definida'}
                        </span>
                        {!isReadOnly && (
                          <button 
                            onClick={() => setEditandoVencimento(true)} 
                            className="btn-editar-mini"
                            title="Editar vencimento"
                          >
                            ✏️
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Filtros */}
            <div className="filtros-card">
              <h3>{isAbastecimento ? 'Abastecimentos da Fatura' : 'Ordens de Serviço da Fatura'}</h3>
              <div className="filtros-grid">
                <div className="filtro-group">
                  <input
                    type="text"
                    name="busca"
                    value={filtros.busca}
                    onChange={handleFiltroChange}
                    placeholder="Buscar por nº ordem ou código..."
                  />
                </div>
                <div className="filtro-group">
                  <select
                    name="status"
                    value={filtros.status}
                    onChange={handleFiltroChange}
                  >
                    <option value="">Todos os status</option>
                    <option value="Aguardando pagamento">Aguardando pagamento</option>
                    <option value="Paga">Paga</option>
                  </select>
                </div>
                {!isReadOnly && (
                  <div className="filtro-group">
                    <button
                      className="btn-selecionar-todas"
                      onClick={handleSelecionarTodas}
                    >
                      {selecionadas.length === ordensServico.filter(os => os.statusPagamento === 'Aguardando pagamento').length
                        ? 'Desmarcar Todas'
                        : 'Selecionar Todas Pendentes'
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Tabela */}
            <div className="table-card">
              <table className="data-table">
                <thead>
                  <tr>
                    {isAbastecimento ? (
                      <>
                        <th style={{ width: '50px' }}></th>
                        <th>Data</th>
                        <th>Cliente</th>
                        <th>Veículo</th>
                        <th>Motorista</th>
                        <th>Combustível</th>
                        <th>Litros</th>
                        <th>Valor</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </>
                    ) : (
                      <>
                        <th style={{ width: '50px' }}></th>
                        <th>Código</th>
                        <th>Nº OS</th>
                        <th>{fatura.tipo === 'Fornecedor' ? 'Cliente' : 'Fornecedor'}</th>
                        <th>Tipo</th>
                        <th>Valor Bruto</th>
                        <th>Valor Líquido</th>
                        <th>Status</th>
                        <th>Data Pagamento</th>
                        <th>Ações</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {isAbastecimento ? (
                    abastecimentosVinculados.length === 0 ? (
                      <tr>
                        <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                          Nenhum abastecimento encontrado
                        </td>
                      </tr>
                    ) : (
                      abastecimentosVinculados.map(item => {
                        const ab = item.abastecimento || item;
                        const isPaga = item.statusPagamento === 'Paga';
                        return (
                          <tr key={ab._id} className={isPaga ? 'row-paga' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selecionadas.includes(ab._id)}
                                onChange={() => handleSelecionar(ab._id)}
                                disabled={isPaga || isReadOnly}
                              />
                            </td>
                            <td>{formatarData(ab.dataReferencia)}</td>
                            <td>{ab.cliente?.razaoSocial || ab.cliente?.nomeFantasia || '-'}</td>
                            <td>{ab.placa || ab.veiculo || '-'}</td>
                            <td>{ab.motorista || '-'}</td>
                            <td>{ab.tipoCombustivel || '-'}</td>
                            <td>{ab.litrosAbastecidos?.toFixed(2) || '-'}</td>
                            <td>{formatarValor(item.valorAbastecimento || ab.valorComDesconto || ab.valor || 0)}</td>
                            <td>
                              <span className={getStatusBadgeClass(item.statusPagamento)}>
                                {item.statusPagamento}
                              </span>
                            </td>
                            <td>
                              {!isPaga && !isReadOnly && (
                                <button
                                  className="btn-icon btn-delete"
                                  onClick={() => handleRemoverOS(ab._id)}
                                  title="Remover da fatura"
                                >
                                  🗑️
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )
                  ) : (
                    ordemFiltradas.map(item => {
                      const os = item.ordemServico;
                      const isPaga = item.statusPagamento === 'Paga';
                      // Calcular valor líquido proporcional para esta OS
                      const valorLiquidoOS = Math.round((item.valorOS || 0) * fatorLiquido * 100) / 100;
                      
                      return (
                        <tr key={os._id} className={isPaga ? 'row-paga' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selecionadas.includes(os._id)}
                              onChange={() => handleSelecionar(os._id)}
                              disabled={isPaga || isReadOnly}
                            />
                          </td>
                          <td><strong>{os.codigo}</strong></td>
                          <td>{os.numeroOrdemServico}</td>
                          <td>
                            {fatura.tipo === 'Fornecedor' 
                              ? (os.cliente?.razaoSocial || os.cliente?.nomeFantasia)
                              : (os.fornecedor?.razaoSocial || os.fornecedor?.nomeFantasia)
                            }
                          </td>
                          <td>{os.tipo?.nome}</td>
                          <td>{formatarValor(item.valorOS || 0)}</td>
                          <td>
                            <span className="valor-liquido" title="Valor após descontar impostos e taxas proporcionais">
                              {formatarValor(valorLiquidoOS)}
                            </span>
                          </td>
                          <td>
                            <span className={getStatusBadgeClass(item.statusPagamento)}>
                              {item.statusPagamento}
                            </span>
                          </td>
                          <td>{formatarData(item.dataPagamento)}</td>
                          <td>
                            {/*
                              O comprovante só faz sentido na OS paga: antes do
                              pagamento não há o que mostrar, e um botão que
                              sempre avisa "não tem" é ruído em toda linha.
                            */}
                            {isPaga && (
                              <button
                                className="btn-icon btn-comprovante"
                                onClick={() => abrirComprovante(item)}
                                disabled={carregandoComprovante === os._id}
                                title="Ver comprovante de pagamento"
                              >
                                {carregandoComprovante === os._id ? '…' : '📎'}
                              </button>
                            )}
                            {!isPaga && !isReadOnly && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() => handleRemoverOS(os._id)}
                                title="Remover da fatura"
                              >
                                🗑️
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Resumo das OS Selecionadas */}
            {resumoSelecionadas && resumoSelecionadas.quantidade > 0 && (
              <div className="resumo-selecionadas">
                <h4>📋 Resumo das {resumoSelecionadas.quantidade} OS Selecionada{resumoSelecionadas.quantidade > 1 ? 's' : ''}</h4>
                <div className="resumo-selecionadas-grid">
                  <div className="resumo-item">
                    <span>Valor Bruto:</span>
                    <strong>{formatarValor(resumoSelecionadas.valorBruto)}</strong>
                  </div>
                  <div className="resumo-item negativo">
                    <span>(-) Taxas Proporcionais:</span>
                    <span>{formatarValor(resumoSelecionadas.taxasProporcional)}</span>
                  </div>
                  <div className="resumo-item destaque">
                    <span>Valor Líquido a Pagar:</span>
                    <strong>{formatarValor(resumoSelecionadas.valorLiquido)}</strong>
                  </div>
                </div>
              </div>
            )}

            {/*
              PROPOSTAS CONJUNTAS DESTA FATURA — uma por conjunto.

              Numa fatura de FORNECEDOR aparece só a parcela dele; o painel
              mostra a irmã ao lado para que ninguém leia o valor da parcela
              como se fosse o da OS inteira. Numa fatura de CLIENTE as duas
              parcelas costumam estar na mesma fatura, e aí o painel é o que
              separa o que é de cada CNPJ.

              O componente busca os números do servidor. A tela não recalcula
              retenção: dois motores dariam dois valores com o mesmo nome.
            */}
            {!isAbastecimento &&
              [...new Map(
                ordensServico
                  .filter((i) => i.ordemServico?.grupoPropostaId)
                  .map((i) => [i.ordemServico.grupoPropostaId, i.ordemServico._id])
              ).values()].map((osId) => (
                <FechamentoPorEmitente key={osId} ordemServicoId={osId} />
              ))}

            {/* Resumo Financeiro */}
            <div className="resumo-financeiro">
              <h3>Resumo Financeiro</h3>
              <div className="resumo-grid">
                {(() => {
                  // Calcular valores separados por peças e serviços
                  let valorTotalPecas = 0;
                  let valorTotalServicos = 0;
                  
                  ordensServico.forEach(item => {
                    valorTotalPecas += item.ordemServico.valorPecas || 0;
                    valorTotalServicos += item.ordemServico.valorServico || 0;
                  });
                  
                  // Calcular detalhamento de impostos primeiro
                  const detalhamentoImpostos = calcularDetalhamentoImpostos();
                  
                  // Usar o valor calculado dinamicamente em vez do salvo no banco
                  // Isso permite ver o detalhamento mesmo em faturas antigas
                  const valorImpostosExibir = detalhamentoImpostos.total || (fatura.valorImpostos || 0);
                  
                  const valorAposImpostos = (fatura.valorComDesconto || 0) - valorImpostosExibir;
                  
                  return (
                    <>
                      <div className="resumo-linha">
                        <span>Valor Total Peças:</span>
                        <strong>{formatarValor(valorTotalPecas)}</strong>
                      </div>
                      <div className="resumo-linha">
                        <span>Valor Total Serviços:</span>
                        <strong>{formatarValor(valorTotalServicos)}</strong>
                      </div>
                      <div className="resumo-linha">
                        <span>(-) Desconto Contrato:</span>
                        <span className="valor-negativo">{formatarValor(fatura.valorDesconto || 0)}</span>
                      </div>
                      <div className="resumo-linha destaque">
                        <span>Valor com Desconto:</span>
                        <strong>{formatarValor(fatura.valorComDesconto || 0)}</strong>
                      </div>
                      {valorImpostosExibir > 0 && (
                        <>
                          <div className="resumo-linha">
                            <span>(-) Impostos & Retenções:</span>
                            <span className="valor-negativo">{formatarValor(valorImpostosExibir)}</span>
                          </div>
                          {detalhamentoImpostos.detalhamento.map((item, idx) => (
                            <div key={idx} style={{ marginLeft: '20px', fontSize: '0.9em', color: '#666' }}>
                              <div style={{ marginTop: '8px', fontWeight: '500' }}>
                                {item.titulo}: {formatarValor(item.valor)}
                              </div>
                              {item.subitens.map((sub, subIdx) => (
                                <div key={subIdx} style={{ marginLeft: '15px', fontSize: '0.85em', color: '#888', marginTop: '3px' }}>
                                  • {sub.texto}
                                </div>
                              ))}
                            </div>
                          ))}
                          <div className="resumo-linha destaque">
                            <span>Valor após Impostos & Retenções:</span>
                            <strong>{formatarValor(valorAposImpostos)}</strong>
                          </div>
                        </>
                      )}
                      {(() => {
                        // CORREÇÃO: Recalcular taxa sobre valor após impostos
                        let valorTaxaExibir = fatura.valorTaxasOperacao || 0;
                        if (percentualTaxaOperacao !== null && valorAposImpostos > 0) {
                          valorTaxaExibir = Math.round(valorAposImpostos * percentualTaxaOperacao / 100 * 100) / 100;
                        }
                        const valorDevidoCorreto = valorAposImpostos - valorTaxaExibir;
                        const valorPagoExibir = fatura.valorPago || 0;
                        const valorRestanteExibir = Math.round((valorDevidoCorreto - valorPagoExibir) * 100) / 100;
                        
                        return (
                          <>
                            {valorTaxaExibir > 0 && (
                              <div className="resumo-linha">
                                <span>{getTextoTaxaOperacao()}:</span>
                                <span className="valor-negativo">{formatarValor(valorTaxaExibir)}</span>
                              </div>
                            )}
                            <div className="resumo-linha destaque-total">
                              <span>VALOR DEVIDO:</span>
                              <strong>{formatarValor(valorDevidoCorreto)}</strong>
                            </div>
                            <div className="resumo-linha pago">
                              <span>Valor Pago:</span>
                              <strong>{formatarValor(valorPagoExibir)}</strong>
                            </div>
                            <div className="resumo-linha restante">
                              <span>Valor Restante:</span>
                              <strong>{formatarValor(valorRestanteExibir)}</strong>
                            </div>
                          </>
                        );
                      })()}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Abate de crédito de recompensa — só em fatura de Fornecedor com taxa */}
            {fatura.tipo === 'Fornecedor' && (fatura.valorTaxasOperacao || 0) > 0 && (
              <RecompensaAbate faturaId={fatura._id} onAbatido={loadFatura} />
            )}

            {/* Botões de ação */}
            <div className="acoes-footer">
              <button
                className="btn-secondary"
                onClick={() => navigate('/faturados')}
              >
                Fechar
              </button>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {fatura.tipo === 'Cliente' ? (
                  // Fatura de Cliente: mostrar os 3 botões
                  <>
                    <button
                      className="btn-info"
                      onClick={imprimirCompleta}
                      title="Imprimir fatura completa"
                    >
                      🖨️ Fatura Completa
                    </button>
                    <button
                      className="btn-info"
                      onClick={imprimirPecas}
                      title="Imprimir somente peças"
                    >
                      🖨️ Somente Peças
                    </button>
                    <button
                      className="btn-info"
                      onClick={imprimirServicos}
                      title="Imprimir somente serviços"
                    >
                      🖨️ Somente Serviços
                    </button>
                  </>
                ) : (
                  // Fatura de Fornecedor: apenas botão completo
                  <button
                    className="btn-info"
                    onClick={imprimirCompleta}
                    title="Imprimir fatura completa"
                  >
                    🖨️ Fatura Completa
                  </button>
                )}
                {!isReadOnly && (
                  <>
                    {osPendentes.length > 0 && (
                      <button
                        className="btn-success"
                        onClick={handlePagarFaturaInteira}
                        title={`Pagar todas as ${osPendentes.length} OS pendentes`}
                      >
                        💰 Pagar Fatura Inteira ({formatarValor(fatura.valorRestante || 0)})
                      </button>
                    )}
                    <button
                      className="btn-primary"
                      onClick={handleMarcarComoPagas}
                      disabled={selecionadas.length === 0}
                    >
                      Marcar como Paga{selecionadas.length > 1 ? 's' : ''} ({selecionadas.length})
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />

      {/* Comprovante de pagamento de uma ordem de serviço desta fatura */}
      {comprovante && (
        <div className="modal-overlay" onClick={() => setComprovante(null)}>
          <div className="modal-content modal-comprovante" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📎 Comprovante de Pagamento</h2>
              <button className="btn-close" onClick={() => setComprovante(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="comprovante-info">
                <p><strong>OS:</strong> {comprovante.codigo} — {comprovante.numeroOrdemServico}</p>
                <p><strong>Valor:</strong> {formatarValor(comprovante.valorOS || 0)}</p>
                <p><strong>Pago em:</strong> {formatarData(comprovante.dataPagamento)}</p>
              </div>

              {/*
                Imagem aparece na tela; PDF e qualquer outro formato viram link.
                Tentar embutir PDF em `img` mostraria um quadro quebrado, e o
                comprovante é justamente o que a pessoa precisa conseguir abrir.
              */}
              <div className="comprovante-preview">
                {String(comprovante.conteudo).startsWith('data:image') ? (
                  <img src={comprovante.conteudo} alt={`Comprovante da OS ${comprovante.codigo}`} />
                ) : (
                  <a
                    href={comprovante.conteudo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    📥 Abrir comprovante
                  </a>
                )}
              </div>

              <div className="comprovante-acoes">
                <a
                  href={comprovante.conteudo}
                  download={`comprovante-${comprovante.codigo || comprovante.osId}`}
                  className="btn-secondary"
                >
                  ⬇️ Baixar
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FaturadoDetalhes;
